import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, InputAdornment, Select, FormControl, InputLabel, MenuItem, Modal, Typography, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import moment from "moment";
import CustomOutlinedSwitch from '../Common/CustomOutlinedSwitch';
import { useNavigate, useParams } from 'react-router-dom';
import { getTenantCourseDetail } from '../../features/tenantCourseDetail/tenantCourseDetailAction';

const useStyles = makeStyles({
    assignCourseHeading: {
    fontWeight: '600 !important',
  },
});

export default function TenantAccess(props) {
  const classes = useStyles(); 
  const { rows, setRows } = props;
  const dispatch = useDispatch();
  const searchDebounceRef = useRef();
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [showAllowAccess, setShowAllowAccess] = useState(false);
  const [showRevokeAccess, setShowRevokeAccess] = useState(false);
  const [pendingToggleId, setPendingToggleId] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [courseName, setCourseName] = useState([]);
  const { id: courseIdFromUrl } = useParams();
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const tenantCourseDetail = useSelector((state) => state.tenantCourseDetail);
  const selectedTenant = tenants.find((row) => row.id === pendingToggleId);

  useEffect(()=> {
    if (tenantCourseDetail?.data?.tenantList) {
      const filteredData = tenantCourseDetail?.data?.tenantList?.map((row, index) => ({
        id: row?.tenantId || `${row?.name || "customer"}-${index}`,
        tenant: row?.name,
        status: row?.status,
        expiration: row?.expirationDate ? moment(row.expirationDate).format("DD-MM-YYYY") : "",
        createdOn: row?.created?.on ? moment(row.created.on).format("DD-MM-YYYY") : "",
        access: row?.access ?? false,
      }));
      setTenants(filteredData)
    }
    if (tenantCourseDetail?.data?.courseList?.length) {
      const filteredCourseName = tenantCourseDetail?.data?.courseList?.map((row) => ({
        id: row?._id,
        name: row?.name,
      }));
      setCourseName(filteredCourseName);
    }
  }, [tenantCourseDetail])
  
  useEffect(()=> {
      if (!hasFetched.current) {
        getTenantDetailsData();
        hasFetched.current = true;
      }
  },[])

  useEffect(() => {
    if (courseName.length && courseIdFromUrl) {
      const matchedCourse = courseName.find((c) => c.id === courseIdFromUrl);
      if (matchedCourse) {
        setSelectedCourseId(matchedCourse.id);
      }
    }
  }, [courseName, courseIdFromUrl]);

  const getTenantDetailsData = (searchValue = "") => {
      dispatch(getTenantCourseDetail({ search: !!searchValue, searchText: searchValue, id: selectedCourseId || courseIdFromUrl }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      getTenantDetailsData(value);
    }, 400);
  };

  const handleSearchClear = () => {
    setSearchText("");
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    getTenantDetailsData("");
  };

  const handleSearch = () => {
    getTenantDetailsData(searchText);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const columns = [
    {
      field: 'tenant',
      headerName: 'Customer',
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              cursor: 'pointer',
              textDecoration: 'none',
              color: '#1976d2',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      renderCell: (params) => (
        <Box
          sx={{
            color: params.value  === 'Active' ? '#11b511': 'red',
            fontWeight: 600,
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 0.6,
    },
    {
      field: 'expiration',
      headerName: 'Expiration',
      flex: 0.6,
    },
    {
      field: 'access',
      headerName: 'Access',
      flex: 0.6,
      renderCell: (params) => (
        <Box sx={{display: 'ruby', height: '20px', width: '20px'}}>
            <CustomOutlinedSwitch
                checked={params.row.access}
                onChange={(e) => handleToggleAccess(params.row.id, e.target.checked)}
            />
        </Box>
      ),
    },
  ];

  const handleCourseChange = (event) => {
    const selectedId = event.target.value;
    setSelectedCourseId(selectedId);
    navigate(`/customer/access/${selectedId}`, { replace: false });
    dispatch(getTenantCourseDetail({ id: selectedId }));
  };

  const handleCloseModal = () => {
    setShowAllowAccess(false);
    setShowRevokeAccess(false);
    setPendingToggleId(null);
  };

  const handleAllowAccess = () => {
    setTenants((prev) =>
      prev.map((row) =>
        row.id === pendingToggleId ? { ...row, access: true } : row
      )
    );
    setShowAllowAccess(false);
    setShowAllowAccess(false);
    setPendingToggleId(null);
  }

  const handleRevokeAccess = () => {
    setTenants((prev) =>
      prev.map((row) =>
        row.id === pendingToggleId ? { ...row, access: false } : row
      )
    );
    setShowRevokeAccess(false);
    setPendingToggleId(null);
   }

  const handleToggleAccess = (id, checked) => {
    setPendingToggleId(id);
    if (checked) {
      setShowAllowAccess(true);
    } else {
      setShowRevokeAccess(true);
    }
  };


  return (
    <Box sx={{ width: "100%", padding: "20px", cursor: "pointer", marginTop: "50px" }}>
        <Box sx={{ display: "flex", width: '100%', justifyContent: 'space-between'}}>
            <FormControl required 
            sx={{
                marginBottom: "16px",
                minWidth: '300px',
                "& .MuiInputLabel-asterisk": { color: "red" },
            }}
            >
            <InputLabel>Select Course</InputLabel>
            <Select label="Select Course" value={selectedCourseId} onChange={handleCourseChange}>
              {courseName.map((course) => (
                <MenuItem key={course?.id} value={course?.id}>
                  {course?.name}
                </MenuItem>
              ))}
            </Select>
            </FormControl>
            <Box sx={{ width: "300px", margin: "0px 0 20px 0px" }}>
            <TextField fullWidth variant="outlined" placeholder="Search course" value={searchText} onChange={handleSearchChange} onKeyDown={handleKeyDown} 
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                    {searchText ? (
                        <IconButton onClick={handleSearchClear}>
                        <CloseIcon />
                        </IconButton>
                    ) : (
                        <IconButton onClick={handleSearch}>
                        <SearchIcon />
                        </IconButton>
                    )}
                    </InputAdornment>
                ),
                }}
            />
            </Box>
        </Box>
      <DataGrid rows={tenants} columns={columns} disableColumnMenu disableColumnReorder disableSelectionOnClick pageSizeOptions={[20]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 20,
            },
          },
        }}
        sx={{
          width: "100%",
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "700",
            color: "#1976d2",
          },
        }}
      />
      {showAllowAccess && <>
          <Modal open={showAllowAccess} onClose={handleCloseModal}>
              <Box style={{ width: '550px', borderRadius: '16px', position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "white", boxShadow: 24, p: 4}}>
              <Typography variant="h6" component="h2" sx={{padding: '20px 0px', fontWeight: '600', color: '#1976d2', textAlign: 'center', borderBottom: '1px solid lightgrey', fontSize: '20px'}}>
                  Allow Access
              </Typography>
              <Box sx={{padding: '32px', display: 'flex', flexDirection: 'column', gap: '20px'}}>
                  <Typography variant="h6" component="h2" sx={{ fontSize: '18px' , textAlign: 'center'}}>
                      Are you sure you want to allow the access to <br/>
                      <strong>{selectedTenant?.tenant || ""}</strong> course for this user?
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: "center", gap: '20px' }}>
                  <Button variant="contained" onClick={handleCloseModal} sx={{textTransform: 'capitalize', fontWeight: '600', background: 'transparent', border: '1px solid #1976d2', color: '#1976d2', borderRadius: '10px'}}>
                      Cancel
                  </Button>
                  <Button variant="contained" onClick={handleAllowAccess} sx={{textTransform: 'capitalize', fontWeight: '600', borderRadius: '10px'}}>
                      Yes, Allow Access
                  </Button>
                  </Box>
              </Box>
              </Box>
          </Modal>
      </>}
      {showRevokeAccess && <>
          <Modal open={showRevokeAccess} onClose={handleCloseModal}>
              <Box style={{ width: '550px', borderRadius: '16px', position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "white", boxShadow: 24, p: 4}}>
              <Typography variant="h6" component="h2" sx={{padding: '20px 0px', fontWeight: '600', color: '#1976d2', textAlign: 'center', borderBottom: '1px solid lightgrey', fontSize: '20px'}}>
                  Revoke Access
              </Typography>
              <Box sx={{padding: '32px', display: 'flex', flexDirection: 'column', gap: '20px'}}>
                  <Typography variant="h6" component="h2" sx={{ fontSize: '18px' , textAlign: 'center'}}>
                      Are you sure you want to revoke the access to <br/>
                      <strong>{selectedTenant?.tenant || ""}</strong> course for this user?
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: "center", gap: '20px' }}>
                  <Button variant="contained" onClick={handleCloseModal} sx={{textTransform: 'capitalize', fontWeight: '600', background: 'transparent', border: '1px solid #1976d2', color: '#1976d2', borderRadius: '10px'}}>
                      Cancel
                  </Button>
                  <Button variant="contained" onClick={handleRevokeAccess} sx={{textTransform: 'capitalize', fontWeight: '600', borderRadius: '10px'}}>
                      Yes, Revoke Access
                  </Button>
                  </Box>
              </Box>
              </Box>
          </Modal>
      </>}
    </Box>
  );
}

